import "./program/conditionalProgramTabs.css";
import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  Users,
  Star,
  ClipboardCheck,
  Send,
  CheckCircle,
  XCircle,
  Clock,
  UserCheck,
  DollarSign,
  Calendar,
  ChevronRight,
  AlertCircle,
  Loader2,
  Eye,
  Mail,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const TryoutsManagement = ({ program, onUpdate }) => {
  const { token, user } = useAuth();
  const [tryouts, setTryouts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [statsState, setStatsState] = useState("loading");
  const [teamsState, setTeamsState] = useState("loading");
  const [selectedTryout, setSelectedTryout] = useState(null);
  const [showEvaluationModal, setShowEvaluationModal] = useState(false);
  const [showDecisionModal, setShowDecisionModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [saving, setSaving] = useState(false);

  // Evaluation form state
  const [evaluation, setEvaluation] = useState({
    skill_rating: 3,
    athleticism_rating: 3,
    attitude_rating: 3,
    coachability_rating: 3,
    overall_rating: 3,
    notes: "",
    recommended_division: "",
    recommended_team: "",
  });

  // Decision form state
  const [decision, setDecision] = useState({
    decision: "accepted",
    division_id: "",
    team_id: "",
    message: "",
  });

  // Invite form state
  const [invite, setInvite] = useState({
    fee_amount: program?.price || 0,
    payment_deadline: "",
    personal_message: "",
    division_id: "",
    team_id: "",
  });

  const [teams, setTeams] = useState([]);

  useEffect(() => {
    if (program?.program_id) {
      fetchTryouts();
      fetchStats();
      fetchTeams();
    }
  }, [program?.program_id]);

  const fetchTryouts = async () => {
    setLoading(true);
    setLoadError("");
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(
        `${API}/programs/${program.program_id}/tryouts`,
        { headers }
      );
      setTryouts(response.data);
    } catch (error) {
      console.error("Error fetching tryouts:", error);
      setLoadError("Unable to load tryout registrations. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    setStatsState("loading");
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(
        `${API}/programs/${program.program_id}/tryouts/stats`,
        { headers }
      );
      setStats(response.data);
      setStatsState("ready");
    } catch (error) {
      console.error("Error fetching stats:", error);
      setStatsState("error");
    }
  };

  const fetchTeams = async () => {
    setTeamsState("loading");
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(
        `${API}/programs/${program.program_id}/teams`,
        { headers }
      );
      setTeams(response.data);
      setTeamsState("ready");
    } catch (error) {
      console.error("Error fetching teams:", error);
      setTeamsState("error");
    }
  };

  const handleEvaluate = (tryout) => {
    setSelectedTryout(tryout);
    if (tryout.evaluation) {
      setEvaluation(tryout.evaluation);
    } else {
      setEvaluation({
        skill_rating: 3,
        athleticism_rating: 3,
        attitude_rating: 3,
        coachability_rating: 3,
        overall_rating: 3,
        notes: "",
        recommended_division: "",
        recommended_team: "",
      });
    }
    setShowEvaluationModal(true);
  };

  const handleDecision = (tryout) => {
    setSelectedTryout(tryout);
    setDecision({
      decision: "accepted",
      division_id: tryout.evaluation?.recommended_division || "",
      team_id: tryout.evaluation?.recommended_team || "",
      message: "",
    });
    setShowDecisionModal(true);
  };

  const handleSendInvite = (tryout) => {
    setSelectedTryout(tryout);
    const defaultDeadline = new Date();
    defaultDeadline.setDate(defaultDeadline.getDate() + 7);
    
    setInvite({
      fee_amount: program?.price || 0,
      payment_deadline: defaultDeadline.toISOString().split('T')[0],
      personal_message: "",
      division_id: tryout.decision?.division_id || "",
      team_id: tryout.decision?.team_id || "",
    });
    setShowInviteModal(true);
  };

  const submitEvaluation = async () => {
    setSaving(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(
        `${API}/tryouts/${selectedTryout.tryout_registration_id}/evaluate`,
        {
          tryout_registration_id: selectedTryout.tryout_registration_id,
          ...evaluation,
        },
        { headers }
      );
      toast.success("Evaluation saved!");
      setShowEvaluationModal(false);
      fetchTryouts();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save evaluation");
    } finally {
      setSaving(false);
    }
  };

  const submitDecision = async () => {
    setSaving(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(
        `${API}/tryouts/${selectedTryout.tryout_registration_id}/decision`,
        {
          tryout_registration_id: selectedTryout.tryout_registration_id,
          ...decision,
        },
        { headers }
      );
      toast.success(`Player ${decision.decision}!`);
      setShowDecisionModal(false);
      fetchTryouts();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save decision");
    } finally {
      setSaving(false);
    }
  };

  const submitInvite = async () => {
    setSaving(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.post(
        `${API}/tryouts/${selectedTryout.tryout_registration_id}/send-invite`,
        {
          tryout_registration_id: selectedTryout.tryout_registration_id,
          ...invite,
        },
        { headers }
      );
      toast.success("Invite sent successfully!");
      setShowInviteModal(false);
      fetchTryouts();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send invite");
    } finally {
      setSaving(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      registered: { color: "bg-blue-100 text-blue-700", icon: Clock },
      evaluated: { color: "bg-purple-100 text-purple-700", icon: ClipboardCheck },
      accepted: { color: "bg-green-100 text-green-700", icon: CheckCircle },
      declined: { color: "bg-red-100 text-red-700", icon: XCircle },
      waitlisted: { color: "bg-yellow-100 text-yellow-700", icon: Clock },
      invite_declined: { color: "bg-gray-100 text-gray-700", icon: XCircle },
    };
    const config = statusConfig[status] || statusConfig.registered;
    const Icon = config.icon;
    return (
      <Badge className={`${config.color} gap-1`}>
        <Icon className="w-3 h-3" />
        {status.replace("_", " ").toUpperCase()}
      </Badge>
    );
  };

  const RatingStars = ({ value, onChange, readonly = false, label = "Overall rating" }) => {
    return (
      <div role="group" aria-label={label} className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            aria-label={`${star} ${star === 1 ? "star" : "stars"}`}
            aria-pressed={star === value}
            type="button"
            onClick={() => !readonly && onChange(star)}
            className={`${readonly ? 'cursor-default' : 'cursor-pointer hover:scale-110'} transition-transform`}
            disabled={readonly}
          >
            <Star
              className={`w-5 h-5 ${
                star <= value
                  ? "fill-yellow-400 text-yellow-400"
                  : "text-gray-300"
              }`}
            />
          </button>
        ))}
      </div>
    );
  };

  const teamChoicesFeedback = (
    <div className="conditional-form-status">
      {teamsState === "loading" && <p role="status">Loading team choices…</p>}
      {teamsState === "error" && <div role="alert"><p>Unable to load team choices.</p><Button variant="outline" onClick={fetchTeams}>Retry team choices</Button></div>}
      {teamsState === "ready" && teams.length === 0 && <p>No teams available. You can leave the team unassigned.</p>}
    </div>
  );

  if (loading) {
    return (
      <div role="status" aria-live="polite" className="program-conditional flex items-center justify-center gap-3 py-8">
        <Loader2 className="w-5 h-5 animate-spin" />
        Loading tryout registrations…
      </div>
    );
  }

  if (loadError) {
    return <div role="alert" className="program-conditional program-data-panel">
      <p>{loadError}</p>
      <Button variant="outline" onClick={fetchTryouts}>Try again</Button>
    </div>;
  }

  return (
    <div className="program-section program-conditional program-tryouts" data-testid="tryouts-management">
      <div className="program-section-heading"><div><h2>Tryouts</h2><p>Evaluate players, record decisions and send registration invites.</p></div></div>
      {statsState === "loading" && <p role="status">Loading tryout totals…</p>}
      {statsState === "error" && <div role="alert" className="program-data-panel"><p>Unable to load tryout totals.</p><Button variant="outline" onClick={fetchStats}>Retry tryout totals</Button></div>}
      {teamsState === "loading" && <p role="status">Loading team choices…</p>}
      {teamsState === "error" && <div role="alert" className="program-data-panel"><p>Unable to load team choices.</p><Button variant="outline" onClick={fetchTeams}>Retry team choices</Button></div>}
      {/* Stats Overview */}
      {statsState === "ready" && stats && (
        <div className="program-section-metrics conditional-tryout-metrics">
          <div className="bg-white border border-gray-200 rounded-2xl p-4">
            <div className="flex items-center gap-2 text-gray-500 mb-1">
              <Users className="w-4 h-4" />
              <span className="text-sm">Total</span>
            </div>
            <p className="text-2xl font-bold text-black">{stats.total}</p>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4">
            <div className="flex items-center gap-2 text-blue-600 mb-1">
              <Clock className="w-4 h-4" />
              <span className="text-sm">Registered</span>
            </div>
            <p className="text-2xl font-bold text-blue-700">{stats.registered}</p>
          </div>
          <div className="bg-purple-50 border border-purple-200 rounded-2xl p-4">
            <div className="flex items-center gap-2 text-purple-600 mb-1">
              <ClipboardCheck className="w-4 h-4" />
              <span className="text-sm">Evaluated</span>
            </div>
            <p className="text-2xl font-bold text-purple-700">{stats.evaluated}</p>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-2xl p-4">
            <div className="flex items-center gap-2 text-green-600 mb-1">
              <CheckCircle className="w-4 h-4" />
              <span className="text-sm">Accepted</span>
            </div>
            <p className="text-2xl font-bold text-green-700">{stats.accepted}</p>
          </div>
          <div className="bg-red-50 border border-red-200 rounded-2xl p-4">
            <div className="flex items-center gap-2 text-red-600 mb-1">
              <XCircle className="w-4 h-4" />
              <span className="text-sm">Declined</span>
            </div>
            <p className="text-2xl font-bold text-red-700">{stats.declined}</p>
          </div>
          <div className="bg-yellow-50 border border-yellow-200 rounded-2xl p-4">
            <div className="flex items-center gap-2 text-yellow-600 mb-1">
              <Clock className="w-4 h-4" />
              <span className="text-sm">Waitlisted</span>
            </div>
            <p className="text-2xl font-bold text-yellow-700">{stats.waitlisted}</p>
          </div>
        </div>
      )}

      {/* Invites Stats */}
      {statsState === "ready" && stats?.invites && (
        <div className="program-data-panel conditional-invite-summary">
          <h4 className="font-semibold text-indigo-800 mb-3 flex items-center gap-2">
            <Mail className="w-4 h-4" />
            Invite Status
          </h4>
          <div className="conditional-invite-metrics">
            <div>
              <p className="text-lg font-bold text-indigo-700">{stats.invites.sent}</p>
              <p className="text-xs text-indigo-600">Sent</p>
            </div>
            <div>
              <p className="text-lg font-bold text-purple-700">{stats.invites.viewed}</p>
              <p className="text-xs text-purple-600">Viewed</p>
            </div>
            <div>
              <p className="text-lg font-bold text-green-700">{stats.invites.accepted}</p>
              <p className="text-xs text-green-600">Accepted</p>
            </div>
            <div>
              <p className="text-lg font-bold text-red-700">{stats.invites.declined}</p>
              <p className="text-xs text-red-600">Declined</p>
            </div>
            <div>
              <p className="text-lg font-bold text-gray-700">{stats.invites.expired}</p>
              <p className="text-xs text-gray-600">Expired</p>
            </div>
          </div>
        </div>
      )}

      {/* Tryouts List */}
      {tryouts.length === 0 ? (
        <div className="program-empty-state">
          <Users className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <h3 className="font-semibold text-gray-700 mb-1">No Tryout Registrations Yet</h3>
          <p className="text-sm text-gray-500">
            Players can register for tryouts from the public program page.
          </p>
        </div>
      ) : (
        <div className="conditional-table-panel">
          <div className="program-table-scroll">
            <table className="w-full" role="table" aria-label="Tryout registrations">
              <thead role="rowgroup" className="bg-gray-50 border-b border-gray-200">
                <tr role="row">
                  <th role="columnheader" scope="col" className="text-left px-4 py-3 text-sm font-semibold text-gray-700">Player</th>
                  <th role="columnheader" scope="col" className="text-left px-4 py-3 text-sm font-semibold text-gray-700">Position</th>
                  <th role="columnheader" scope="col" className="text-left px-4 py-3 text-sm font-semibold text-gray-700">Status</th>
                  <th role="columnheader" scope="col" className="text-left px-4 py-3 text-sm font-semibold text-gray-700">Rating</th>
                  <th role="columnheader" scope="col" className="text-left px-4 py-3 text-sm font-semibold text-gray-700">Invite</th>
                  <th role="columnheader" scope="col" className="text-right px-4 py-3 text-sm font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody role="rowgroup" className="divide-y divide-gray-100">
                {tryouts.map((tryout) => (
                  <tr role="row" key={tryout.tryout_registration_id} className="hover:bg-gray-50">
                    <td role="cell" data-label="Player" className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div aria-hidden="true" className="conditional-player-avatar w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                          {tryout.player?.photo_url ? (
                            <img
                              src={tryout.player.photo_url}
                              alt=""
                              className="w-10 h-10 rounded-full object-cover"
                            />
                          ) : (
                            <span className="text-sm font-medium text-gray-600">
                              {tryout.player?.first_name?.[0]}{tryout.player?.last_name?.[0]}
                            </span>
                          )}
                        </div>
                        <div>
                          <p id={`tryout-player-${tryout.tryout_registration_id}`} className="font-medium text-black">
                            {tryout.player?.first_name} {tryout.player?.last_name}
                          </p>
                          <p className="text-xs text-gray-500">
                            Age {tryout.player?.age || "N/A"} • {tryout.parent?.email}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td role="cell" data-label="Position" className="px-4 py-3">
                      <span className="text-sm text-gray-600">
                        {tryout.preferred_position || "Not specified"}
                      </span>
                    </td>
                    <td role="cell" data-label="Status" className="px-4 py-3">
                      {getStatusBadge(tryout.status)}
                    </td>
                    <td role="cell" data-label="Rating" className="px-4 py-3">
                      {tryout.evaluation ? (
                        <div className="flex items-center gap-1">
                          <RatingStars value={tryout.evaluation.overall_rating} readonly />
                          <span className="text-sm text-gray-500 ml-1">
                            ({tryout.evaluation.overall_rating}/5)
                          </span>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400">Not evaluated</span>
                      )}
                    </td>
                    <td role="cell" data-label="Invite" className="px-4 py-3">
                      {tryout.invite_sent ? (
                        <Badge className="bg-indigo-100 text-indigo-700">
                          <Send className="w-3 h-3 mr-1" />
                          Sent
                        </Badge>
                      ) : tryout.status === "accepted" ? (
                        <Badge className="bg-gray-100 text-gray-600">Ready to Send</Badge>
                      ) : (
                        <span className="text-sm text-gray-400">-</span>
                      )}
                    </td>
                    <td role="cell" data-label="Actions" className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEvaluate(tryout)}
                          className="text-xs"
                          aria-describedby={`tryout-player-${tryout.tryout_registration_id}`}
                          data-testid={`evaluate-btn-${tryout.tryout_registration_id}`}
                        >
                          <ClipboardCheck className="w-3 h-3 mr-1" />
                          {tryout.evaluation ? "Edit" : "Evaluate"}
                        </Button>
                        
                        {tryout.evaluation && !["accepted", "declined", "waitlisted"].includes(tryout.status) && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDecision(tryout)}
                            className="text-xs border-green-300 text-green-700 hover:bg-green-50"
                            aria-describedby={`tryout-player-${tryout.tryout_registration_id}`}
                          data-testid={`decision-btn-${tryout.tryout_registration_id}`}
                          >
                            <UserCheck className="w-3 h-3 mr-1" />
                            Decision
                          </Button>
                        )}
                        
                        {tryout.status === "accepted" && !tryout.invite_sent && (
                          <Button
                            size="sm"
                            onClick={() => handleSendInvite(tryout)}
                            className="text-xs conditional-primary"
                            aria-describedby={`tryout-player-${tryout.tryout_registration_id}`}
                          data-testid={`invite-btn-${tryout.tryout_registration_id}`}
                          >
                            <Send className="w-3 h-3 mr-1" />
                            Send Invite
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Evaluation Modal */}
      <Dialog open={showEvaluationModal} onOpenChange={setShowEvaluationModal}>
        <DialogContent className="program-conditional conditional-dialog max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardCheck className="w-5 h-5 text-purple-600" />
              Player Evaluation
            </DialogTitle>
            <DialogDescription>Record ratings, recommendations and notes for this player.</DialogDescription>
          </DialogHeader>
          
          {selectedTryout && (
            <div className="space-y-4">
              {/* Player Info */}
              <div className="bg-gray-50 rounded-lg p-3 flex items-center gap-3">
                <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center">
                  <span className="font-medium text-gray-600">
                    {selectedTryout.player?.first_name?.[0]}{selectedTryout.player?.last_name?.[0]}
                  </span>
                </div>
                <div>
                  <p className="font-semibold text-black">
                    {selectedTryout.player?.first_name} {selectedTryout.player?.last_name}
                  </p>
                  <p className="text-sm text-gray-500">
                    Age {selectedTryout.player?.age} • {selectedTryout.preferred_position || "No position"}
                  </p>
                </div>
              </div>

              {/* Parent Notes */}
              {selectedTryout.parent_notes && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-xs text-blue-600 font-medium mb-1">Parent Notes</p>
                  <p className="text-sm text-blue-800">{selectedTryout.parent_notes}</p>
                </div>
              )}

              {/* Rating Fields */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label>Skill Level</Label>
                  <RatingStars 
                    label="Skill Level"
                    value={evaluation.skill_rating} 
                    onChange={(v) => setEvaluation({...evaluation, skill_rating: v})} 
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Label>Athleticism</Label>
                  <RatingStars 
                    label="Athleticism"
                    value={evaluation.athleticism_rating} 
                    onChange={(v) => setEvaluation({...evaluation, athleticism_rating: v})} 
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Label>Attitude</Label>
                  <RatingStars 
                    label="Attitude"
                    value={evaluation.attitude_rating} 
                    onChange={(v) => setEvaluation({...evaluation, attitude_rating: v})} 
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Label>Coachability</Label>
                  <RatingStars 
                    label="Coachability"
                    value={evaluation.coachability_rating} 
                    onChange={(v) => setEvaluation({...evaluation, coachability_rating: v})} 
                  />
                </div>
                <div className="flex items-center justify-between border-t pt-3">
                  <Label className="font-semibold">Overall Rating</Label>
                  <RatingStars 
                    label="Overall Rating"
                    value={evaluation.overall_rating} 
                    onChange={(v) => setEvaluation({...evaluation, overall_rating: v})} 
                  />
                </div>
              </div>

              {/* Recommendations */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-sm">Recommended Division</Label>
                  <Select
                    value={evaluation.recommended_division}
                    onValueChange={(v) => setEvaluation({...evaluation, recommended_division: v})}
                  >
                    <SelectTrigger aria-label="Recommended Division" className="mt-1">
                      <SelectValue placeholder="Select division" />
                    </SelectTrigger>
                    <SelectContent>
                      {program?.divisions?.map((div) => (
                        <SelectItem key={div.division_id} value={div.division_id}>
                          {div.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm">Recommended Team</Label>
                  <Select
                    value={evaluation.recommended_team}
                    onValueChange={(v) => setEvaluation({...evaluation, recommended_team: v})}
                  >
                    <SelectTrigger aria-label="Recommended Team" className="mt-1">
                      <SelectValue placeholder="Select team" />
                    </SelectTrigger>
                    <SelectContent>
                      {teams.map((team) => (
                        <SelectItem key={team.team_id} value={team.team_id}>
                          {team.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {teamChoicesFeedback}
                </div>
              </div>

              {/* Notes */}
              <div>
                <Label className="text-sm">Evaluation Notes</Label>
                <Textarea
                  className="mt-1"
                  placeholder="Additional observations, strengths, areas for improvement..."
                  rows={3}
                  aria-label="Evaluation Notes"
                    value={evaluation.notes}
                  onChange={(e) => setEvaluation({...evaluation, notes: e.target.value})}
                />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setShowEvaluationModal(false)}>
                  Cancel
                </Button>
                <Button onClick={submitEvaluation} disabled={saving} className="conditional-primary">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Save Evaluation
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Decision Modal */}
      <Dialog open={showDecisionModal} onOpenChange={setShowDecisionModal}>
        <DialogContent className="program-conditional conditional-dialog max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserCheck className="w-5 h-5 text-green-600" />
              Make Decision
            </DialogTitle>
            <DialogDescription>Record a decision and an optional message to the parent.</DialogDescription>
          </DialogHeader>
          
          {selectedTryout && (
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="font-semibold text-black">
                  {selectedTryout.player?.first_name} {selectedTryout.player?.last_name}
                </p>
                {selectedTryout.evaluation && (
                  <p className="text-sm text-gray-500">
                    Overall Rating: {selectedTryout.evaluation.overall_rating}/5
                  </p>
                )}
              </div>

              <div>
                <Label>Decision</Label>
                <Select
                  value={decision.decision}
                  onValueChange={(v) => setDecision({...decision, decision: v})}
                >
                  <SelectTrigger aria-label="Decision" className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="accepted">
                      <span className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        Accept
                      </span>
                    </SelectItem>
                    <SelectItem value="waitlisted">
                      <span className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-yellow-600" />
                        Waitlist
                      </span>
                    </SelectItem>
                    <SelectItem value="declined">
                      <span className="flex items-center gap-2">
                        <XCircle className="w-4 h-4 text-red-600" />
                        Decline
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {decision.decision === "accepted" && (
                <>
                  <div>
                    <Label>Assign to Division</Label>
                    <Select
                      value={decision.division_id}
                      onValueChange={(v) => setDecision({...decision, division_id: v})}
                    >
                      <SelectTrigger aria-label="Assign to Division" className="mt-1">
                        <SelectValue placeholder="Select division" />
                      </SelectTrigger>
                      <SelectContent>
                        {program?.divisions?.map((div) => (
                          <SelectItem key={div.division_id} value={div.division_id}>
                            {div.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Assign to Team (Optional)</Label>
                    <Select
                      value={decision.team_id || "__unassigned__"}
                      onValueChange={(v) => setDecision({...decision, team_id: v === "__unassigned__" ? "" : v})}
                    >
                      <SelectTrigger aria-label="Assign to Team (Optional)" className="mt-1">
                        <SelectValue placeholder="Select team" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__unassigned__">No team yet</SelectItem>
                        {teams.map((team) => (
                          <SelectItem key={team.team_id} value={team.team_id}>
                            {team.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  {teamChoicesFeedback}
                  </div>
                </>
              )}

              <div>
                <Label>Message to Parent (Optional)</Label>
                <Textarea
                  className="mt-1"
                  placeholder="Add a personal note..."
                  rows={2}
                  aria-label="Message to Parent (Optional)"
                    value={decision.message}
                  onChange={(e) => setDecision({...decision, message: e.target.value})}
                />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setShowDecisionModal(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={submitDecision} 
                  disabled={saving}
                  className={
                    decision.decision === "accepted" 
                      ? "bg-green-600 hover:bg-green-700" 
                      : decision.decision === "declined"
                        ? "bg-red-600 hover:bg-red-700"
                        : "bg-yellow-600 hover:bg-yellow-700"
                  }
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Confirm {decision.decision.charAt(0).toUpperCase() + decision.decision.slice(1)}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Send Invite Modal */}
      <Dialog open={showInviteModal} onOpenChange={setShowInviteModal}>
        <DialogContent className="program-conditional conditional-dialog max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="w-5 h-5 text-indigo-600" />
              Send Registration Invite
            </DialogTitle>
            <DialogDescription>Review the fee, deadline and placement before sending an invitation.</DialogDescription>
          </DialogHeader>
          
          {selectedTryout && (
            <div className="space-y-4">
              <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center">
                    <span className="font-medium text-indigo-600">
                      {selectedTryout.player?.first_name?.[0]}{selectedTryout.player?.last_name?.[0]}
                    </span>
                  </div>
                  <div>
                    <p className="font-semibold text-indigo-900">
                      {selectedTryout.player?.first_name} {selectedTryout.player?.last_name}
                    </p>
                    <p className="text-sm text-indigo-600">
                      Invite will be sent to: {selectedTryout.parent?.email}
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="flex items-center gap-1">
                    <DollarSign className="w-3 h-3" />
                    Registration Fee
                  </Label>
                  <Input
                    type="number"
                    className="mt-1"
                    aria-label="Registration Fee"
                    value={invite.fee_amount}
                    onChange={(e) => setInvite({...invite, fee_amount: parseFloat(e.target.value) || 0})}
                  />
                </div>
                <div>
                  <Label className="flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    Payment Deadline
                  </Label>
                  <Input
                    type="date"
                    className="mt-1"
                    aria-label="Payment Deadline"
                    value={invite.payment_deadline}
                    onChange={(e) => setInvite({...invite, payment_deadline: e.target.value})}
                  />
                </div>
              </div>

              <div>
                <Label>Division</Label>
                <Select
                  value={invite.division_id}
                  onValueChange={(v) => setInvite({...invite, division_id: v})}
                >
                  <SelectTrigger aria-label="Division" className="mt-1">
                    <SelectValue placeholder="Select division" />
                  </SelectTrigger>
                  <SelectContent>
                    {program?.divisions?.map((div) => (
                      <SelectItem key={div.division_id} value={div.division_id}>
                        {div.name} - ${div.price}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Team (Optional)</Label>
                <Select
                  value={invite.team_id || "__unassigned__"}
                  onValueChange={(v) => setInvite({...invite, team_id: v === "__unassigned__" ? "" : v})}
                >
                  <SelectTrigger aria-label="Team (Optional)" className="mt-1">
                    <SelectValue placeholder="Select team" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__unassigned__">Not assigned yet</SelectItem>
                    {teams.map((team) => (
                      <SelectItem key={team.team_id} value={team.team_id}>
                        {team.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                  {teamChoicesFeedback}
              </div>

              <div>
                <Label>Personal Message (Optional)</Label>
                <Textarea
                  className="mt-1"
                  placeholder="Congratulations on making the team! We're excited to have you..."
                  rows={3}
                  aria-label="Personal Message (Optional)"
                    value={invite.personal_message}
                  onChange={(e) => setInvite({...invite, personal_message: e.target.value})}
                />
              </div>

              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-sm text-gray-600">
                  <strong>What happens next:</strong>
                </p>
                <ul className="text-sm text-gray-500 mt-1 space-y-1">
                  <li>• Parent receives email with registration invite</li>
                  <li>• They can accept and pay ${invite.fee_amount} online</li>
                  <li>• Payment deadline: {invite.payment_deadline || "Not set"}</li>
                </ul>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setShowInviteModal(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={submitInvite} 
                  disabled={saving || !invite.fee_amount || !invite.payment_deadline}
                  className="conditional-primary"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                  Send Invite
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TryoutsManagement;
